import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { CustomInput } from "global/components";
import EmailEditor, { Design } from "react-email-editor";
import strings from "global/constants/StringConstants";
import { useEffect, useRef, useState } from "react";
import urls from "global/constants/UrlConstants";
import history from "utils/history";
import {
  addCampaignTemplate,
  getCampaignTemplateById,
  getFooterTemplate,
  updateCampaignTemplate,
} from "../TemplatesService";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { CampaignTemplateType, HandleTemplateType } from "models/interfaces";
import { useLocation } from "react-router-dom";
import { selectEmail } from "redux/authSlice";
import { useAppSelector } from "utils/hooks";
import { templateFields } from "../Helpers";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import viewpaths from "global/constants/ViewPathConstants";
import CustomRouterPrompt from "global/components/CustomRouterPrompt/CustomRouterPrompt";
import ConfirmationModal from "./ConfirmationModal";

const DesignTemplate = () => {
  const emailEditorRef = useRef<any>();
  const urlParams = new URLSearchParams(useLocation().search);
  const templateId = urlParams.get("id");
  const [draftJson, setDraftJson] = useState<any>();
  const [defaultFooterTemplate, setDefaultFooterTemplate] = useState<Design>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isBlocking, setIsBlocking] = useState<boolean>(false);
  const [editorLoaded, setEditorLoaded] = useState<boolean>(false);
  const [initialDesign, setInitialDesign] = useState<any>();
  const [formField, setFormField] = useState<any>(() => {
    const urlsState: any = history.location.state;
    const initialFields = templateFields();
    if (urlsState?.templateName) {
      initialFields.templateName.value = urlsState.templateName;
    }
    return initialFields;
  });
  const [isBackButtonClicked, setIsBackButtonClicked] =
    useState<boolean>(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] =
    useState<boolean>(false);
  const userId = useAppSelector(selectEmail);

  useEffect(() => {
    if (isTruthy(templateId)) {
      getTemplateById();
    } else {
      getDefaultFooterTemplate();
    }
  }, []);

  const getDefaultFooterTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await getFooterTemplate();
      setDefaultFooterTemplate(response);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTemplateById = async () => {
    try {
      setIsLoading(true);
      const response = await getCampaignTemplateById(templateId ?? "");
      const isUserTemplate = response.owner === userId;
      handleViewTemplate(response, isUserTemplate);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleViewTemplate = (
    selectedTemplate: HandleTemplateType,
    isUserTemplate: boolean
  ) => {
    const jsonData = JSON.parse(selectedTemplate.jsonContent);
    setIsEditing(true);
    setDraftJson(jsonData);
    setFormField({
      ...formField,
      templateName: {
        ...formField.templateName,
        value:
          selectedTemplate.owner === userId
            ? selectedTemplate.name
            : `Copy of ${selectedTemplate.name}`,
      },
      category: {
        value: selectedTemplate.category ?? "Private",
      },
      isUserTemplate: {
        value: isUserTemplate,
      },
    });
    setIsLoading(false);
  };

  const loadDesign = async () => {
    if (emailEditorRef.current) {
      setEditorLoaded(true);
      if (draftJson) {
        // Edit Template
        emailEditorRef.current.loadDesign(draftJson);
      } else {
        // New Template with Default White Background
        emailEditorRef?.current?.loadDesign(defaultFooterTemplate);
      }
      const design: any = await exportHTML();
      setInitialDesign(design.html);
    }
  };

  const exportHTML = async () => {
    return new Promise((resolve, reject) => {
      emailEditorRef.current.editor?.exportHtml(
        (data: any) => {
          resolve(data);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  };

  const checkForChanges = () => {
    if (!emailEditorRef.current) return false;
    return new Promise<boolean>((resolve) => {
      emailEditorRef.current.editor.exportHtml((data: any) => {
        const { design, html } = data;
        const hasChanges =
          JSON.stringify(html) !== JSON.stringify(initialDesign);

        resolve(hasChanges);
      });
    });
  };

  const handleNavigationAttempt = async () => {
    const hasChanges = await checkForChanges();
    if (hasChanges) {
      setIsBlocking(hasChanges);
    } else {
      history.push(viewpaths.templatesViewPath);
    }
  };

  const handleValidation = () => {
    if (!formField.templateName.value) {
      setFormField({
        ...formField,
        templateName: {
          ...formField.templateName,
          error: "Template name is required",
        },
      });
      return false;
    }
    return true;
  };

  const handleSave = async (unlayerJSON: Design, exitCallBack: Function) => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const template: CampaignTemplateType = {
          category: formField.category.value,
          name: formField.templateName.value.trim(),
          jsonContent: JSON.stringify(unlayerJSON),
          customTools: [urls.customSocialTool, urls.customVideoTool],
          id:
            isEditing &&
            (formField.category.value === "System" ||
              !formField.isUserTemplate.value)
              ? undefined
              : templateId,
        };

        if (template.name && template.jsonContent) {
          const updateTemplate =
            isEditing &&
            formField.category.value !== "System" &&
            formField.isUserTemplate.value
              ? await updateCampaignTemplate(template)
              : await addCampaignTemplate(template);
          openSuccessNotification(updateTemplate.message);
          exitCallBack();
        }
        setIsLoading(false);
        history.push(viewpaths.templatesViewPath);
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExitUnlayer = () => {
    setFormField(templateFields());
    setIsEditing(false);
    setDraftJson(null);
    history.push(viewpaths.templatesViewPath);
  };

  const exit = () => {
    if (isBackButtonClicked) {
      handleExitUnlayer();
    } else {
      setIsBlocking(false);
    }
  };

  const exportHtml = (isSaveAndExit: boolean = false) => {
    if (formField.templateName.value.length <= 50) {
      emailEditorRef.current.editor.exportHtml(async (data: any) => {
        const { design, html } = data;
        await handleSave(design, handleExitUnlayer);
      });
    }
  };

  const handleOnchange = (event: any) => {
    if (event.target.value !== undefined && event.target.value.length <= 40) {
      setFormField({
        ...formField,
        [event.target.name]: {
          ...formField[event.target.name],
          value: event.target.value,
          error: "",
        },
      });
    }
  };

  const getSkeletonPageHeader = () => {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Skeleton animation="wave" variant="text" height={50} width={180} />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Skeleton animation="wave" variant="text" height={50} width={150} />
        </Stack>
      </Stack>
    );
  };

  const getHeader = () => {
    if (!editorLoaded) {
      return getSkeletonPageHeader();
    }
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="outlined"
            onClick={async () => {
              setIsBackButtonClicked(true);
              await handleNavigationAttempt();
            }}
          >
            Go Back
          </Button>
          <Grid item xs={3} spacing={2}>
            <CustomInput
              id="templates_name_field"
              placeHolder="Enter template name"
              required
              type="text"
              name="templateName"
              value={formField.templateName.value}
              onChange={(event: any) => handleOnchange(event)}
              propsToInputElement={{ maxlength: 40 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {formField.templateName.value.length} / 40
                    </span>
                  </InputAdornment>
                ),
              }}
              error={
                !isTruthy(formField.templateName.value) &&
                formField.templateName.error
              }
              helperText={
                !isTruthy(formField.templateName.value)
                  ? formField.templateName.error
                  : ""
              }
            />
          </Grid>
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            display: "inline-flex",
          }}
        >
          <Button
            id="save_template_btn"
            size="small"
            variant="contained"
            disabled={isLoading && !draftJson}
            onClick={() => handleValidation() && setIsSaveButtonClicked(true)}
          >
            Save changes
          </Button>
        </Stack>
      </Stack>
    );
  };

  const handleDialogClose = () => {
    setIsBlocking(false);
  };

  const getDesignTemplate = () => {
    return (
      <Box>
        {((templateId && draftJson) || isTruthy(defaultFooterTemplate)) && (
          <Box>
            <EmailEditor
              ref={emailEditorRef}
              onReady={() => loadDesign()}
              projectId={strings.UNLAYER_PROJECT_ID}
              minHeight={850}
              options={{
                features: {
                  textEditor: {
                    tables: true,
                  },
                },
                appearance: {
                  panels: {
                    tools: {
                      dock: "left",
                    },
                  },
                },
                customJS: [urls.customSocialTool, urls.customVideoTool],
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const getDesignTemplatePage = () => {
    return (
      <Stack p={1} spacing={2}>
        {getHeader()}
        {getDesignTemplate()}
      </Stack>
    );
  };
  return (
    <>
      {getDesignTemplatePage()}
      <CustomLoader isLoading={isLoading} />
      {isBlocking && (
        <CustomRouterPrompt
          isVisible={isBlocking}
          handleDialogClose={handleDialogClose}
          isNavsHide={true}
          exit={exit}
        />
      )}
      {isSaveButtonClicked && (
        <ConfirmationModal
          title="Save Template"
          subtitle2={"Are you sure want to proceed with changed template?"}
          isVisible={isSaveButtonClicked}
          handleDialogClose={() => setIsSaveButtonClicked(false)}
          btn1Title="Cancel"
          btn1OnClick={() => setIsSaveButtonClicked(false)}
          btn2Title="Save"
          btn2OnClick={() => exportHtml(false)}
          isNavsHide={true}
        />
      )}
    </>
  );
};

export default DesignTemplate;
